/* eslint-disable no-underscore-dangle */

/*
	This mixin is used to set different options of Doughnut Chart

	These are not general enough to be included in other charts,
	but are used for doughnut charts and perhaps pie charts.
*/
import chartMixin from './mixins/chart';

export default {
	mixins: [
		chartMixin,
	],

	render(createElement) {
		let centerSlot = null;
		let titleSlot = null;
		const legendItems = [];
		const isInitialized = this.chartArea && this.chartArea.height && this.chartArea.width;
		let longDescription = null;

		// This only goes in circular, not cartesian
		if (isInitialized && this.iconToken) {
			centerSlot = createElement(
				'helio-icon', {
					class: [
						'chart__center-slot',
						'chart__center-slot--icon',
					],
					style: {
						color: this.style[`${this.theme}-font-color`],
						fontFamily: this.style.font,
						fontSize: `${this.chartArea.width / 5}px`,
					},
					props: {
						token: this.iconToken,
						weight: this.iconWeight,
					},
				},
			);
		} else if (isInitialized && this.hasCenterSlot) {
			centerSlot = createElement(
				'div', {
					class: 'chart__center-slot',
					style: {
						color: this.style[`${this.theme}-font-color`],
						fontFamily: this.style.font,
						fontSize: `${this.chartArea.width / 5}px`,
					},
				}, this.$slots.center,
			);
		}

		if (this.useLongDescription) {
			longDescription = createElement(
				'div', {
					class: 'chart__long-description screen-reader-text',
					attrs: {
						id: `long-description-${this._uid}`,
					},
					domProps: {
						innerHTML: this.longDescription,
					},
				},
			);
		}

		if (this.items.length) {
			for (let index = 0; index < this.items.length; index += 1) {
				const item = this.items[index];

				legendItems.push(createElement(
					'li', {
						class: 'chart__legend-item',
						on: {
							click: this.onLegendCLick,
							mouseenter: this.onLegendHover,
							mouseleave: this.onLegendLeave,
						},
						attrs: {
							'data-index': index,
						},
					}, [
						createElement(
							'span', {
								class: 'chart__legend-color',
								style: {
									background: item.fillStyle,
									borderColor: item.strokeStyle,
									borderWidth: `${item.lineWidth}px`,
								},
								attrs: {
									'data-index': index,
								},
							},
						),
						createElement(
							'p', {
								class: 'chart__legend-label',
								style: {
									color: item.fontColor,
									textDecoration: item.hidden ? 'line-through' : '',
								},
								attrs: {
									'data-index': index,
								},
							}, [
								createElement(
									'span', {
										class: 'chart__legend-text',
										attrs: {
											'data-index': index,
										},
									}, [
										item.text,
									],
								),
								createElement(
									'span', {
										class: 'chart__legend-value',
										attrs: {
											'data-index': index,
										},
									}, [
										this.tooltipUnitPrefix + item.value + this.tooltipUnitSuffix,
									],
								),
							],
						),
					],
				));
			}
		}

		if (this.hasTitleSlot) {
			titleSlot = createElement(
				'div', {
					class: 'chart__title',
				}, this.$slots.title,
			);
		}

		return createElement(
			'div', {
				class: 'chart',
			}, [
				// chart
				createElement(
					'div', {
						class: 'chart__graph',
						style: {
							height: this.chartHeight,
							width: this.chartWidth,
						},
					}, [
						// chart canvas
						createElement(
							'canvas', {
								class: 'chart__canvas',
								attrs: {
									id: this.chartId,
									'aria-label': `${this.computedAriaLabel}`,
									'aria-describedby': `long-description-${this._uid}`,
									role: 'img',
								},
								ref: 'canvas',
							},
						),
						// center slot (icon, text, etc.)
						centerSlot,
						// tooltip anchor
						createElement(
							'div', {
								class: 'chart__tooltip-anchor',
								attrs: {
									'data-anchor': `anchor-${this.chartId}-${this._uid}`,
								},
								style: {
									top: this.tooltipAnchorY,
									left: this.tooltipAnchorX,
								},
								ref: `anchor-${this.chartId}-${this._uid}`,
							},
						),
						// tooltip
						createElement(
							'helio-tooltip', {
								class: 'chart__tooltip',
								props: {
									anchor: `anchor-${this.chartId}-${this._uid}`,
									active: !!this.tooltipHtml,
									position: this.horizontal ? 'right' : 'top',
									portal: true,
									skin: this.theme === 'light' ? 'dark' : 'light',
								},
								attrs: {
									class: 'tooltip--chart',
								},
								ref: `tooltip-${this.chartId}-${this._uid}`,
							}, [
								createElement(
									'template', {
										slot: 'content',
									}, [
										createElement(
											'span', {
												domProps: {
													innerHTML: this.tooltipHtml,
												},
											},
										),
									],
								),
							],
						),
					],
				),
				// legend and title areas
				createElement(
					'div', {
						class: 'chart__info',
					}, [
						// title
						titleSlot,
						// legend
						createElement(
							'div', {
								class: 'chart__legend',
								attrs: {
									id: `legend-${this.chartId}-${this._uid}`,
									// 🠋 aria-hidden hides confusing/duplicated screen-reader text
									'aria-hidden': true,
								},
							}, [
								createElement(
									'ul', {
										class: 'chart__legend-list',
									}, legendItems,
								),
							],
						),
					],
				),
				// long description for screen reader
				longDescription,
			],
		);
	},

	props: {
		ariaLabel: {
			type: String,
			required: false,
			default: null,
			description: 'Supplies an aria-label for the chart as a whole. If this prop isn\'t passed, a default aria-label of "graphique de données" / "data graph" will be used.',
		},

		chartWidth: {
			type: String,
			required: false,
			default: '50%',
			validator: width => width.indexOf('px') > -1 || width.indexOf('%') > -1,
			description: 'Sets the width of the graph in px or %',
		},

		cutout: {
			type: String,
			required: false,
			default: '90%',
			validator: width => width.indexOf('%') > -1,
			description: 'Sets the cutout percentage',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the custom icon to be added in the center of the doughnut',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the custom icon to be added in the center of the doughnut',
		},
	},

	slots: {
		center: {
			required: false,
			scoped: true,
			description: 'Supplies the content to appear in the middle of the Doughnut Chart component',
		},

		title: {
			required: true,
			scoped: false,
			description: 'Supplies the title for the Doughnut Chart component',
		},
	},

	data() {
		return {
			activeLegend: null,
			hiddenLegendsArray: [],
			longDescription: '',
		};
	},

	computed: {
		circularOptions() {
			return {
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						enabled: false,
						external: this.customTooltips,
						intersect: true,
					},
				},
				cutout: this.cutout,
			};
		},

		hasCenterSlot() {
			return !!this.$slots.center || !!this.$scopedSlots.center;
		},

		hasTitleSlot() {
			return !!this.$slots.title || !!this.$scopedSlots.title;
		},

		items() {
			if (this.$data._chart) {
				const {
					legend,
					data,
				} = this.$data._chart;

				return legend.legendItems.reduce((acc, curr) => {
					const item = {
						fillStyle: curr.fillStyle,
						stokeStyle: curr.stokeStyle,
						linewidth: curr.linewidth,
						hidden: curr.hidden,
						text: curr.text,
						index: curr.index,
						value: data.datasets[0].data[curr.index],
					};

					acc.push(item);

					return acc;
				}, []);
			}

			return [];
		},

		computedAriaLabel() {
			const backupAriaLabel = this.$t('circular-chart.aria-label');
			return this.ariaLabel || backupAriaLabel;
		},

		totalValue() {
			const data = [...this.chartData.datasets];
			const dataValues = data.map(item => item.data);
			const total = dataValues.reduce((previousValue, currentValue) => previousValue
				+ currentValue, 0);
			return total;
		},
	},

	watch: {
		// eslint-disable-next-line func-names
		'$i18n.locale': function () {
			this.generateLongDescription();
		},
	},

	mounted() {
		this.generateLongDescription();
	},

	methods: {
		customTooltips(tooltipModel) {
			this.tooltipHtml = null;

			// Hide if no tooltip
			if (tooltipModel.tooltip.opacity === 0) {
				return;
			}

			const { dataIndex } = tooltipModel.tooltip.dataPoints[0];

			// Hide if data is hidden
			if (tooltipModel.chart.legend.legendItems[dataIndex].hidden) {
				return;
			}

			const tooltipAnchorVerticalOffset = -8;

			this.tooltipAnchorY = `${tooltipModel.tooltip.caretY + tooltipAnchorVerticalOffset}px`;
			this.tooltipAnchorX = `${tooltipModel.tooltip.caretX}px`;

			const getBody = (bodyItem) => bodyItem.lines;

			// Set text
			if (tooltipModel.tooltip.body) {
				const bodyLines = tooltipModel.tooltip.body.map(getBody);

				// If many lines are showing, this is a bug, it should not happen, hide them.
				if (bodyLines.length > 1) return;

				let innerHtml = '';

				bodyLines.forEach((body, i) => {
					const dataValue = body[0].split(': ')[body[0].split(': ').length - 1];

					const colors = tooltipModel.tooltip.labelColors[i];
					const bodyStyle = `background: ${colors.backgroundColor}; border: none;`;

					const span = `<span class="tooltip__label-icon" style=" ${bodyStyle}"></span>`;
					const data = this.tooltipUnitPrefix + dataValue + this.tooltipUnitSuffix;

					let percentage = 0;
					if (this.totalValue > 0) {
						percentage = Math.round((Number(dataValue) / this.totalValue) * 100);
					}

					innerHtml += `<span class="tooltip__label">${span}${data} (${percentage}%)</span>`;
				});

				this.$nextTick(() => {
					this.tooltipHtml = innerHtml;
				});
			}
		},

		generateLegend() {
			if (this.$data._chart) {
				return this.$data._chart.generateLegend();
			}
			return null;
		},

		onLegendCLick($event) {
			if (this.$data._chart) {
				const { type } = this.$data._chart.config;

				if (type === 'pie' || type === 'doughnut') {
					const dataIndex = $event.target.attributes['data-index'].value;

					this.$data._chart.toggleDataVisibility(dataIndex);

					this.$data._chart.update();

					const dataIndexInArray = this.hiddenLegendsArray.indexOf(dataIndex);

					if (dataIndexInArray > -1) {
						this.hiddenLegendsArray.splice(dataIndexInArray, 1);
					} else {
						this.hiddenLegendsArray.push(dataIndex);
					}

					if (this.$data._chart.legend.legendItems[dataIndex].hidden) {
						this.customTooltips({
							tooltip: {
								opacity: 0,
							},
						});
					} else {
						const prevPosition = {};
						const displayInterval = setInterval(() => {
							this.delayDisplayTooltip(this.$data._chart, dataIndex, prevPosition, displayInterval);
						}, 100);
					}

					$event.stopPropagation();
				}
			}
		},

		onLegendHover($event) {
			const index = $event.target.attributes['data-index'].value;
			const arc = this.$data._chart.getDatasetMeta(0).data[index];
			const dataset = this.$data._chart.data.datasets[0];

			const tooltipModel = {
				tooltip: {
					opacity: 1,
					caretY: arc.tooltipPosition().y,
					caretX: arc.tooltipPosition().x,
					body: [
						{
							lines: [
								dataset.data[index].toString(),
							],
						},
					],
					labelColors: [
						{
							backgroundColor: dataset.backgroundColor[index],
						},
					],
					dataPoints: [
						{
							dataIndex: index,
						},
					],
				},
			};

			this.customTooltips({
				chart: this.$data._chart,
				tooltip: {
					...tooltipModel.tooltip,
				},
			});

			$event.stopPropagation();
		},

		onLegendLeave($event) {
			this.customTooltips({
				tooltip: {
					opacity: 0,
				},
			});

			$event.stopPropagation();
		},

		delayDisplayTooltip(chart, dataIndex, prevPosition, displayInterval) {
			const arc = chart.getDatasetMeta(0).data[dataIndex];

			if (prevPosition.y !== arc.tooltipPosition().y
				&& prevPosition.x !== arc.tooltipPosition().x
			) {
				prevPosition.y = arc.tooltipPosition().y;
				prevPosition.x = arc.tooltipPosition().x;
				return;
			}

			clearInterval(displayInterval);

			const dataset = chart.data.datasets[0];

			const tooltipModel = {
				tooltip: {
					opacity: 1,
					caretY: arc.tooltipPosition().y,
					caretX: arc.tooltipPosition().x,
					body: [
						{
							lines: [
								dataset.data[dataIndex].toString(),
							],
						},
					],
					labelColors: [
						{
							backgroundColor: dataset.backgroundColor[dataIndex],
						},
					],
					dataPoints: [
						{
							dataIndex,
						},
					],
				},
			};

			this.customTooltips({
				chart,
				tooltip: {
					...tooltipModel.tooltip,
				},
			});
		},

		generateLongDescription() {
			// **get title**
			const title = this.$t('circular-chart.simple-title');

			// **get description**
			let description = '';

			const data = [...this.chartData.datasets];
			const descriptionArr = [];
			data.forEach(entry => descriptionArr.push(`<li>${this.$t('circular-chart.text-description', {
				datasetLabel: entry.datasetLabel,
				data: entry.data,
				unit: this.tooltipUnitSuffix,
			})}</li>`));
			description = descriptionArr.join(' ');

			this.longDescription = `<p class="chart__long-description">${title} <ul>${description}</ul></p>`;

			// **set long description**
			this.longDescription = `<p class="chart__long-description">${title} <ul>${description}</ul></p>`;
		},
	},
};
